import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, CircularProgress,
  IconButton, Tooltip, Snackbar, Alert, Divider, Avatar,
} from '@mui/material';
import {
  ArrowLeft, Copy, CheckCircle2, ShieldCheck, Lock,
} from 'lucide-react';
import logoPainel from '../imagens/logopainelftm.png.ico';
import trackEvent from '../utils/trackEvent';

const TESTIMONIALS = [
  {
    nome: 'Lucas Andrade',
    cargo: 'Gestor de tráfego',
    texto:
      'BM verificada em 2h. Salvou minha campanha do Facebook que estava em análise há 3 semanas. Vale cada centavo.',
    iniciais: 'LA',
  },
  {
    nome: 'Maria Eduarda',
    cargo: 'Agência de marketing',
    texto:
      'Já uso pra todos os clientes. Aprovação altíssima e zero burocracia. Recomendo demais.',
    iniciais: 'ME',
  },
  {
    nome: 'Rafael Costa',
    cargo: 'E-commerce',
    texto:
      'Esperava ter que comprar domínio, hospedagem, contratar dev… o painel resolveu tudo em minutos. Surreal.',
    iniciais: 'RC',
  },
];

const formatBRL = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [paid, setPaid] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [secondsLeft, setSecondsLeft] = useState(null);
  const purchaseFiredRef = useRef(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const tokenStorage = useMemo(() => localStorage.getItem('token'), []);

  // Carrega pedido + QR
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${tokenStorage}` };
        const [orderRes, qrRes] = await Promise.all([
          fetch(`${apiUrl}/api/pagamento/order/${orderId}`, { headers }),
          fetch(`${apiUrl}/api/pagamento/pix-qrcode/${orderId}`, { headers }),
        ]);

        const orderJson = await orderRes.json();
        const qrJson = await qrRes.json();

        if (!alive) return;

        if (!orderJson.success) {
          setErrorMsg(orderJson.message || 'Pedido não encontrado.');
          setLoading(false);
          return;
        }

        setOrder(orderJson.order);
        if (orderJson.order.status === 'paid') {
          setPaid(true);
        }

        if (qrJson.success) {
          setQr(qrJson.qr);
          if (qrJson.qr.expirationDate) {
            const exp = new Date(qrJson.qr.expirationDate).getTime();
            const diff = Math.max(0, Math.floor((exp - Date.now()) / 1000));
            setSecondsLeft(diff);
          }
        } else if (orderJson.order.status !== 'paid') {
          setErrorMsg(qrJson.message || 'Não foi possível carregar o QR Code.');
        }
      } catch {
        if (alive) setErrorMsg('Erro ao carregar checkout. Tente novamente.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [apiUrl, orderId, tokenStorage]);

  // Contador regressivo do QR
  useEffect(() => {
    if (secondsLeft === null) return;
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => (s !== null ? Math.max(0, s - 1) : s)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  // Polling de status
  useEffect(() => {
    if (paid || !orderId) return;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const resp = await fetch(`${apiUrl}/api/pagamento/status/${orderId}`, {
          headers: { Authorization: `Bearer ${tokenStorage}` },
        });
        const data = await resp.json();
        if (data.status === 'paid') {
          setPaid(true);
          clearInterval(interval);
          if (!purchaseFiredRef.current && order) {
            purchaseFiredRef.current = true;
            trackEvent('Purchase', {
              value: order.valor,
              contentName: 'Tokens Painel FTM',
              eventId: `purchase_${orderId}`,
              pixelData: {
                content_category: 'Tokens',
                content_ids: ['token-ftm'],
                num_items: order.quantidadeTokens || 1,
              },
            });
          }
          // Atualiza dados do user no localStorage
          try {
            const meRes = await fetch(`${apiUrl}/api/auth/me`, {
              headers: { Authorization: `Bearer ${tokenStorage}` },
            });
            const meJson = await meRes.json();
            if (meJson.success && meJson.user) {
              localStorage.setItem('user', JSON.stringify(meJson.user));
            }
          } catch {}
          setSnack({ open: true, message: 'Pagamento confirmado! Seus tokens foram liberados.', severity: 'success' });
          setTimeout(() => navigate('/dashboard'), 3500);
        }
      } catch {}
      if (attempts > 200) clearInterval(interval); // ~10min @3s
    }, 3000);
    return () => clearInterval(interval);
  }, [apiUrl, orderId, tokenStorage, paid, order, navigate]);

  const copy = async () => {
    if (!qr?.payload) return;
    try {
      await navigator.clipboard.writeText(qr.payload);
      setCopied(true);
      setSnack({ open: true, message: 'Código Pix copiado!', severity: 'success' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setSnack({ open: true, message: 'Não foi possível copiar. Selecione manualmente.', severity: 'error' });
    }
  };

  const formatTimer = (s) => {
    if (s === null) return null;
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <Box sx={{ background: '#0B0F1A', minHeight: '100vh', pb: 6 }}>
      {/* Topbar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(17, 24, 39, 0.85)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(124, 58, 237, 0.15)',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              <img src={logoPainel} alt="FTM" style={{ width: 36, height: 36, borderRadius: 8 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff' }}>
                PAINEL <span style={{ color: '#7C3AED' }}>FTM</span>
              </Typography>
            </Box>
            <Button
              startIcon={<ArrowLeft size={18} strokeWidth={1.75} />}
              onClick={() => navigate('/comprar-tokens')}
              sx={{ color: '#9CA3AF', fontWeight: 600, '&:hover': { color: '#fff' } }}
            >
              Voltar
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pt: { xs: 2.5, sm: 4 } }}>
        {/* Cabeçalho */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3.5 } }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.6, fontSize: { xs: '1.15rem', sm: '1.3rem' } }}>
            Pagamento via Pix
          </Typography>
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
            Tokens liberados automaticamente após o pagamento.
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#7C3AED' }} />
          </Box>
        )}

        {!loading && errorMsg && (
          <Alert severity="error" sx={{ maxWidth: 520, mx: 'auto' }}>{errorMsg}</Alert>
        )}

        {!loading && order && (
          <Box sx={{ maxWidth: 720, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.8 }}>
            {/* Topo: QR + dados de pagamento */}
            <Paper
              sx={{
                p: { xs: 2.5, sm: 3 }, borderRadius: 3,
                background: '#141926',
                border: '1px solid rgba(255,255,255,0.06)',
                width: '100%', maxWidth: 460, mx: 'auto',
              }}
            >
              {paid ? (
                <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 1.8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(124,58,237,0.12)',
                    border: '1px solid rgba(124,58,237,0.3)',
                  }}>
                    <CheckCircle2 size={28} strokeWidth={1.75} color="#A78BFA" />
                  </Box>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                    Pagamento confirmado
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', mb: 2, fontSize: '0.85rem' }}>
                    Seus {order.quantidadeTokens} token{order.quantidadeTokens > 1 ? 's' : ''} foram liberado{order.quantidadeTokens > 1 ? 's' : ''}. Redirecionando…
                  </Typography>
                  <Button size="small" variant="contained" onClick={() => navigate('/dashboard')} sx={{ borderRadius: 2, px: 3 }}>
                    Ir para o Dashboard
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Resumo do pedido */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                        Pedido
                      </Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
                        {order.tipo === 'coins'
                          ? `${order.quantidadeCoins} coin${order.quantidadeCoins > 1 ? 's' : ''}`
                          : `${order.quantidadeTokens} token${order.quantidadeTokens > 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                        Total
                      </Typography>
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.3rem', lineHeight: 1 }}>
                        {formatBRL(order.valor)}
                      </Typography>
                      {order.desconto > 0 && (
                        <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', fontWeight: 600, mt: 0.3 }}>
                          {order.desconto}% off aplicado
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 2 }} />

                  {/* QR */}
                  {qr ? (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, alignItems: 'center', mb: 2 }}>
                        <Box sx={{
                          p: 1, borderRadius: 2, background: '#fff',
                        }}>
                          <img
                            src={`data:image/png;base64,${qr.encodedImage}`}
                            alt="QR Code Pix"
                            style={{ width: 150, height: 150, display: 'block' }}
                          />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography sx={{ color: '#E5E7EB', fontWeight: 600, fontSize: '0.82rem' }}>
                            Escaneie no app do seu banco
                          </Typography>
                          {secondsLeft !== null && secondsLeft > 0 && (
                            <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', mt: 0.3 }}>
                              Expira em {formatTimer(secondsLeft)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Pix copia e cola */}
                      <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', mb: 0.6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Pix copia e cola
                      </Typography>
                      <Box sx={{
                        display: 'flex', alignItems: 'stretch', gap: 0.6,
                        p: 0.5, borderRadius: 2,
                        background: '#0B0F1A',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}>
                        <Box sx={{
                          flex: 1, px: 1.2, py: 0.9,
                          fontFamily: 'monospace', fontSize: '0.72rem', color: '#9CA3AF',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}>
                          {qr.payload}
                        </Box>
                        <Tooltip title={copied ? 'Copiado' : 'Copiar'} arrow>
                          <IconButton
                            onClick={copy}
                            size="small"
                            sx={{
                              borderRadius: 1.5,
                              background: '#7C3AED',
                              color: '#fff',
                              px: 1.4,
                              '&:hover': { background: '#6D28D9' },
                            }}
                          >
                            {copied ? <CheckCircle2 size={15} strokeWidth={2} /> : <Copy size={15} strokeWidth={2} />}
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Status / aguardando */}
                      <Box sx={{
                        mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                      }}>
                        <CircularProgress size={11} sx={{ color: '#6B7280' }} />
                        <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                          Aguardando pagamento
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Alert severity="warning">QR Code indisponível no momento. Atualize a página em alguns segundos.</Alert>
                  )}
                </>
              )}
            </Paper>

            {/* Linha de selos discretos */}
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
              gap: { xs: 1.5, sm: 3 }, py: 0.5,
            }}>
              {[
                { icon: <Lock size={13} strokeWidth={1.75} />, t: 'Pagamento via Asaas' },
                { icon: <ShieldCheck size={13} strokeWidth={1.75} />, t: 'Compra protegida' },
                { icon: <CheckCircle2 size={13} strokeWidth={1.75} />, t: 'Crédito automático' },
              ].map((b, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#6B7280' }}>
                  {b.icon}
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 500 }}>{b.t}</Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 1 }} />

            {/* Stats numéricos */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: { xs: 3, sm: 5 }, py: 1,
            }}>
              {[
                { v: '+10.000', l: 'BMs verificadas' },
                { v: '99%', l: 'Aprovação' },
                { v: '<3h', l: 'Tempo médio' },
              ].map((s, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>
                    {s.v}
                  </Typography>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.68rem', fontWeight: 500, mt: 0.3 }}>
                    {s.l}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 1 }} />

            {/* Depoimentos */}
            <Typography sx={{
              color: '#6B7280', fontSize: '0.7rem', fontWeight: 600,
              textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5,
            }}>
              Quem já usa o painel
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.2,
            }}>
              {TESTIMONIALS.map((t, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.6, borderRadius: 2,
                    background: '#141926',
                    border: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', flexDirection: 'column', gap: 1,
                  }}
                >
                  <Typography sx={{ color: '#D1D5DB', fontSize: '0.78rem', lineHeight: 1.5 }}>
                    {t.texto}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                    <Avatar sx={{
                      width: 26, height: 26,
                      background: 'rgba(124,58,237,0.15)',
                      color: '#A78BFA',
                      fontSize: '0.65rem', fontWeight: 700,
                      border: '1px solid rgba(124,58,237,0.25)',
                    }}>
                      {t.iniciais}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#E5E7EB', fontWeight: 600, fontSize: '0.74rem', lineHeight: 1.2 }}>
                        {t.nome}
                      </Typography>
                      <Typography sx={{ color: '#6B7280', fontSize: '0.66rem' }}>
                        {t.cargo}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
